import React, {
  FC,
  useState,
  ChangeEvent,
  useEffect,
  Fragment,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styled from "@emotion/styled";
import {
  AppBar,
  Backdrop,
  Card,
  Tabs,
  Tab,
  ClickAwayListener,
  Chip,
  Stack,
  Box,
  Tooltip,
} from "@mui/material";
import SimpleBarReact from "simplebar-react";
import All from "components/Search/All";
import People from "components/Search/People";
import Club from "components/Search/Club";
import Stocks from "components/Search/Stocks";
import Topics from "components/Search/Topics";
import Sectors from "components/Search/Sectors";
import SearchInput from "components/SearchInput";
import { CLICK_SEARCH, POST_SEARCH, SEARCH_TAG_ADD } from "utills/events";
import ReactGA from "react-ga4";
import { useHistory, useLocation } from "react-router-dom";
import {
  ProfileInfoQuery,
  ProfileInfoDocument,
  ProfileInfoQueryVariables,
  GetHashTagDetailsQuery,
  GetHashTagDetailsQueryVariables,
  GetHashTagDetailsDocument,
  GetDollarTagDetailsQuery,
  GetDollarTagDetailsDocument,
  GetDollarTagDetailsQueryVariables,
  GetClubQuery,
  GetClubQueryVariables,
  GetClubDocument,
} from "api";
import { isEmpty } from "lodash-es";
import { ApolloQueryResult, useApolloClient } from "@apollo/client";

type TabType = "ALL" | "PEOPLE" | "CLUB" | "STOCKTAG" | "SECTORS" | "HASHTAG";

export interface SearchTag {
  name: string;
  id: string | number;
  tag: "people" | "club" | "dollar" | "topic";
  username?: string;
  hashtagname?: string;
  dollarTagType?: string;
  slug?: string;
}

const searchObject = {
  people: "userId",
  club: "clubId",
  dollar: "dollarId",
  topic: "hashTagId",
};

const Search: FC = () => {
  const client = useApolloClient();
  const inputRef = useRef(null);
  const history = useHistory();
  const [value, setValue] = useState<TabType>("ALL");
  const [search, setSearch] = useState<string>("");

  const [searchChip, setSearchChip] = useState<Array<SearchTag>>([]);

  const deleteChip = (e: React.MouseEvent, value: string) => {
    e?.preventDefault();
    if (inputRef.current) {
      (inputRef.current as any)?.focus();
    }
    setSearchChip(searchChip?.filter((chip) => chip?.name != value));
  };

  const closeSearch = () => {
    setSearch("");
  };

  const handleChange = (event: React.SyntheticEvent, newValue: TabType) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (search.length > 1) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [search]);

  const { search: searchTwo } = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(searchTwo),
    [searchTwo]
  );

  const hashTagId = searchParams.get("hashTagId");
  const userId = searchParams.get("userId");
  const dollarId = searchParams.get("dollarId");
  const clubId = searchParams.get("clubId");

  useEffect(() => {
    if (
      isEmpty(searchParams.get("hashTagId")) &&
      isEmpty(searchParams.get("userId")) &&
      isEmpty(searchParams.get("dollarId")) &&
      isEmpty(searchParams.get("clubId"))
    )
      setSearchChip([]);
  }, [searchParams, setSearchChip]);

  const handleGetFilterPosts = useCallback(
    (data: SearchTag | undefined) => {
      ReactGA.event(POST_SEARCH, {});
      let searchChipTemp = [...searchChip];
      if (data) {
        searchChipTemp = searchChipTemp.filter((x) => x.tag != data.tag);
        searchChipTemp.push(data);
        setSearchChip(searchChipTemp);
      }
      if (searchChipTemp.length === 1) {
        const newData = searchChipTemp[0];

        switch (newData.tag) {
          case "people":
            {
              history.push(`/profile/${newData?.username}`);
            }
            break;
          case "club":
            {
              history.push(`/club/${newData?.id}`);
            }
            break;
          case "dollar":
            {
              history.push(
                `/${newData?.dollarTagType?.toLocaleLowerCase()}/${newData?.slug
                }/${newData?.id}`
              );
            }
            break;
          case "topic":
            {
              history.push(`/hashtag/${newData?.hashtagname}`);
            }
            break;
        }
      } else {
        const object = searchChipTemp.reduce((a, c) => {
          return { ...a, [searchObject[c.tag]]: c.id };
        }, {});
        history.push({
          pathname: "/filter-posts",
          search: new URLSearchParams(object).toString(),
        });
      }
    },
    [searchChip, history, setSearchChip]
  );

  const handleAddChip = useCallback(
    (data: SearchTag, fromApi?: boolean) => {
      console.log("Add Chip", inputRef.current);
      if (inputRef.current && !fromApi) {
        (inputRef.current as any)?.focus();
      }
      ReactGA.event(SEARCH_TAG_ADD, {});
      let searchChipTemp = [...searchChip];
      if (data) {
        searchChipTemp = searchChipTemp.filter((x) => x.tag != data.tag);
        searchChipTemp.push(data);
        setSearchChip(searchChipTemp);
      }
    },
    [searchChip, inputRef, setSearchChip]
  );

  useEffect(() => {

    const requests: Array<Promise<ApolloQueryResult<any>>> = [];
    if (userId) {
      requests.push(
        client.query<ProfileInfoQuery, ProfileInfoQueryVariables>({
          query: ProfileInfoDocument,
          variables: { data: { userId } }
        })
      )
    }
    if (hashTagId) {
      requests.push(
        client.query<GetHashTagDetailsQuery, GetHashTagDetailsQueryVariables>({
          query: GetHashTagDetailsDocument,
          variables: { hashtagId: hashTagId }
        })
      )
    }
    if (dollarId) {
      requests.push(
        client.query<GetDollarTagDetailsQuery, GetDollarTagDetailsQueryVariables>({
          query: GetDollarTagDetailsDocument,
          variables: { dollarTagId: Number(dollarId) }
        })
      )
    }
    if (clubId) {
      requests.push(
        client.query<GetClubQuery, GetClubQueryVariables>({
          query: GetClubDocument,
          variables: { clubId }
        })
      )
    }
    console.log("Requests", requests);
    Promise.all(requests).then(values => {
      const newSearchChip: Array<SearchTag> = values.map(({ data }) => {
        if (data?.getClub) return {
          id: `${data?.getClub?.clubId}`,
          name: `${data?.getClub?.clubName}`,
          tag: "club",
        }
        if (data?.getProfileInfo) return {
          id: userId as string,
          name: `${data?.getProfileInfo?.firstName} ${data?.getProfileInfo?.lastName}`,
          tag: "people",
          username: `${data?.getProfileInfo?.username}`,
        }
        if (data?.getDollarTagDetails) return {
          id: Number(data?.getDollarTagDetails?.dollarTagId),
          name: data?.getDollarTagDetails?.dollarTagName as string,
          tag: "dollar",
          slug: `${data?.getDollarTagDetails?.slug}`,
          dollarTagType: data?.getDollarTagDetails?.dollarTagType,
        }
        if (data?.getHashTagDetails) return {
          id: hashTagId as string,
          name: data?.getHashTagDetails?.hashtag?.hashtagName as string,
          tag: "topic",
          hashtagname: data?.getHashTagDetails?.hashtag?.hashtagName as string,
        }
        return null;
      }).filter(x => Boolean(x)) as Array<SearchTag>;
      setSearchChip(newSearchChip);
    })
  }, [hashTagId, userId, dollarId, clubId, setSearchChip])

  const CustomChip = (
    <ChipSearchBox>
      {" "}
      <Stack direction="row" gap={1} alignItems="center">
        {searchChip?.map((chip, index) => (
          <Tooltip title={chip?.name} key={index}>
            <Chip
              key={index}
              label={chip?.name}
              variant="outlined"
              onDelete={(e) => deleteChip(e, chip?.name)}
              sx={{
                maxWidth: "100px",
                width: "100%",
                borderColor: "rgba(27, 106, 201, 1)",
                backgroundColor: "rgba(27, 106, 201, 0.1)",
                ".MuiChip-deleteIcon": {
                  color: "rgba(27, 106, 201, 1)",
                  position: "relative",
                  marginRight: "13px",
                },
              }}
            />
          </Tooltip>
        ))}
      </Stack>
    </ChipSearchBox>
  );

  return (
    <Fragment>
      <ClickAwayListener onClickAway={closeSearch}>
        <SearchInputWraper>
          <SearchInput
            searchChip={searchChip}
            customchip={CustomChip}
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              // ReactGA.event(CLICK_SEARCH, {
              //   event_label: event.target.value,
              // });
              setSearch(event.target.value);
            }}
            handleGetFilterPosts={handleGetFilterPosts}
            inputRef={inputRef}
          ></SearchInput>

          {search.length > 1 && (
            <SearchContent variant="outlined" elevation={2}>
              <CustomAppBar color="default" position="static">
                <CustomTabs
                  variant="fullWidth"
                  value={value}
                  onChange={handleChange}
                >
                  <Tab label="All" value="ALL" />
                  <Tab label="People" value="PEOPLE" />
                  <Tab label="Clubs" value="CLUB" />
                  <Tab label="Stocks" value="STOCKTAG" />
                  <Tab label="Sectors" value="SECTORS" />
                  <Tab label="Topics" value="HASHTAG" />
                </CustomTabs>
              </CustomAppBar>
              <TabPanel>
                {value === "ALL" && (
                  <All
                    search={search}
                    close={closeSearch}
                    isAnchor={searchChip?.length < 1}
                    handleAddChip={handleAddChip}
                    handleGetFilterPosts={handleGetFilterPosts}
                  />
                )}
                {value === "PEOPLE" && (
                  <People
                    search={search}
                    close={closeSearch}
                    isAnchor={searchChip?.length < 1}
                    handleAddChip={handleAddChip}
                    handleGetFilterPosts={handleGetFilterPosts}
                  />
                )}
                {value === "CLUB" && (
                  <Club
                    search={search}
                    close={closeSearch}
                    isAnchor={searchChip?.length < 1}
                    handleAddChip={handleAddChip}
                    handleGetFilterPosts={handleGetFilterPosts}
                  />
                )}
                {value === "STOCKTAG" && (
                  <Stocks
                    search={search}
                    close={closeSearch}
                    isAnchor={searchChip?.length < 1}
                    handleAddChip={handleAddChip}
                    handleGetFilterPosts={handleGetFilterPosts}
                  />
                )}
                {value === "SECTORS" && (
                  <Sectors
                    search={search}
                    close={closeSearch}
                    isAnchor={searchChip?.length < 1}
                    handleAddChip={handleAddChip}
                    handleGetFilterPosts={handleGetFilterPosts}
                  />
                )}
                {value === "HASHTAG" && (
                  <Topics
                    search={search}
                    close={closeSearch}
                    isAnchor={searchChip?.length < 1}
                    handleAddChip={handleAddChip}
                    handleGetFilterPosts={handleGetFilterPosts}
                  />
                )}
              </TabPanel>
            </SearchContent>
          )}
        </SearchInputWraper>
      </ClickAwayListener>

      <Backdrop open={search?.length > 0} />
    </Fragment>
  );
};

export default Search;

const CustomAppBar = styled(AppBar)``;
const ChipSearchBox = styled(Box)`
  display: flex;
  justify-content: space-between;
`;

const CustomTabs = styled(Tabs)`
  && {
    .MuiTab-root {
      min-width: 60px;
    }
  }
`;

const TabPanel = styled(SimpleBarReact)`
  max-height: 400px;
  height: 100%;
`;

const SearchContent = styled(Card)`
  width: -webkit-fill-available;
  box-sizing: border-box;
  border-radius: 4px;
  margin: 8px 2px;
  overflow-y: auto;
  border: none;
  position: absolute;
  width: 100%;
`;

const SearchInputWraper = styled.div`
  width: min(480px, 100%);
  margin: auto;
  position: relative;
  z-index: 999;
  display: none;
  @media (min-width: 960px) {
    display: inline-block;
  }
  @media (min-width: 1281px) {
    width: min(600px, 100%);
  }
`;
